/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable sonarjs/cognitive-complexity */
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useSearchParams } from 'react-router-dom';

import { useIsPlotPage } from '@/hooks/useIsPlotPage';
import { usePathSet } from '@/hooks/usePathSet';
import { useWorkstreamId } from '@/pages/shared/home/hooks/useWorkstreamId';
import { buildPath } from '@/utils/buildPath';

import { BREADCRUMB_TYPE, BreadcrumbItem } from './constants';

const useBreacrumbRouteConfig = (): {
  /**
   * The path key from usePathSet() that corresponds to this page.
   */
  pathKey: string;
  /**
   * key for the page label (e.g. 'navbar.carbon').
   */
  pageLabelKey: string;
  /**
   * key for the parent section label (e.g. 'navbar.measurements').
   */
  sectionLabelKey?: string;
}[] => {
  const { t } = useTranslation();

  return [
    // Land
    { pathKey: 'plots', pageLabelKey: t('navbar.plots'), sectionLabelKey: t('navbar.land') },
    { pathKey: 'groups', pageLabelKey: t('navbar.groups'), sectionLabelKey: t('navbar.land') },

    // Measurements
    { pathKey: 'carbonDetails', pageLabelKey: t('navbar.carbon'), sectionLabelKey: t('navbar.measurements') },
    { pathKey: 'waterDetails', pageLabelKey: t('navbar.water'), sectionLabelKey: t('navbar.measurements') },
    {
      pathKey: 'biodiversityDetails',
      pageLabelKey: t('navbar.biodiversity'),
      sectionLabelKey: t('navbar.measurements'),
    },
    { pathKey: 'soilDetails', pageLabelKey: t('navbar.soil'), sectionLabelKey: t('navbar.measurements') },
    { pathKey: 'socialDetails', pageLabelKey: t('navbar.social'), sectionLabelKey: t('navbar.measurements') },

    // Intelligence Tools
    { pathKey: 'comparisons', pageLabelKey: t('navbar.comparisons'), sectionLabelKey: t('navbar.intelligenceTools') },
    { pathKey: 'compare', pageLabelKey: t('navbar.comparisons'), sectionLabelKey: t('navbar.intelligenceTools') },
    {
      pathKey: 'scenarioModels',
      pageLabelKey: t('navbar.scenarioModelling'),
      sectionLabelKey: t('navbar.intelligenceTools'),
    },
    {
      pathKey: 'scenarioModelsPhysicalRisk',
      pageLabelKey: t('navbar.scenarioModelling'),
      sectionLabelKey: t('navbar.intelligenceTools'),
    },
    {
      pathKey: 'scenarioModelsFinancialImpact',
      pageLabelKey: t('navbar.scenarioModelling'),
      sectionLabelKey: t('navbar.intelligenceTools'),
    },
    {
      pathKey: 'scenarioModelsMethodology',
      pageLabelKey: t('navbar.scenarioModelling'),
      sectionLabelKey: t('navbar.intelligenceTools'),
    },
    {
      pathKey: 'disclosure',
      pageLabelKey: t('navbar.disclosure'),
      sectionLabelKey: t('navbar.intelligenceTools'),
    },
    {
      pathKey: 'disclosureDependencies',
      pageLabelKey: t('navbar.disclosure'),
      sectionLabelKey: t('navbar.intelligenceTools'),
    },
    {
      pathKey: 'disclosureImpacts',
      pageLabelKey: t('navbar.disclosure'),
      sectionLabelKey: t('navbar.intelligenceTools'),
    },
    { pathKey: 'reports', pageLabelKey: t('navbar.reports'), sectionLabelKey: t('navbar.intelligenceTools') },
    { pathKey: 'copilot', pageLabelKey: t('navbar.va'), sectionLabelKey: t('navbar.intelligenceTools') },

    // Portfolio
    {
      pathKey: 'portfolioNaturalCapital',
      pageLabelKey: t('navbar.natureCapital'),
      sectionLabelKey: t('navbar.portfolio'),
    },
    { pathKey: 'portfolioForecasts', pageLabelKey: t('navbar.forecasts'), sectionLabelKey: t('navbar.portfolio') },
    { pathKey: 'portfolioInvestments', pageLabelKey: t('navbar.investments'), sectionLabelKey: t('navbar.portfolio') },
    { pathKey: 'portfolioUnits', pageLabelKey: t('navbar.units'), sectionLabelKey: t('navbar.portfolio') },
    { pathKey: 'portfolioStocks', pageLabelKey: t('navbar.stocks'), sectionLabelKey: t('navbar.portfolio') },
    { pathKey: 'portfolioContracts', pageLabelKey: t('navbar.contracts'), sectionLabelKey: t('navbar.portfolio') },

    // Settings
    {
      pathKey: 'account',
      pageLabelKey: t('shared.account.sidebar.personalInformation'),
      sectionLabelKey: t('navbar.settings'),
    },
    {
      pathKey: 'accountEditEmail',
      pageLabelKey: t('shared.account.editEmail.title'),
      sectionLabelKey: t('navbar.settings'),
    },
    {
      pathKey: 'accountEditName',
      pageLabelKey: t('shared.account.editName.title'),
      sectionLabelKey: t('navbar.settings'),
    },
    {
      pathKey: 'accountEditPassword',
      pageLabelKey: t('shared.account.editPassword.title'),
      sectionLabelKey: t('navbar.settings'),
    },
    { pathKey: 'accountLegal', pageLabelKey: t('shared.account.sidebar.legal'), sectionLabelKey: t('navbar.settings') },
    {
      pathKey: 'accountTeam',
      pageLabelKey: t('shared.account.sidebar.team'),
      sectionLabelKey: t('navbar.settings'),
    },
    {
      pathKey: 'accountTeamInvite',
      pageLabelKey: t('shared.account.team.buttons.invite'),
      sectionLabelKey: t('navbar.settings'),
    },
    {
      pathKey: 'accountTeamEditMember',
      pageLabelKey: t('shared.account.teamEditMember.labels.title'),
      sectionLabelKey: t('navbar.settings'),
    },
    { pathKey: 'workstreams', pageLabelKey: t('navbar.workstreams'), sectionLabelKey: t('navbar.settings') },
    {
      pathKey: 'workstreamsAdd',
      pageLabelKey: t('global.workstream.actions.add'),
      sectionLabelKey: t('navbar.settings'),
    },
    {
      pathKey: 'workstreamsEdit',
      pageLabelKey: t('global.workstream.labels.workstream'),
      sectionLabelKey: t('navbar.settings'),
    },
    { pathKey: 'uploads', pageLabelKey: t('navbar.settings') },
    {
      pathKey: 'uploadsPlotOverviewPlot',
      pageLabelKey: t('navbar.plotOverview'),
      sectionLabelKey: t('navbar.settings'),
    },
  ];
};

export const useBreadcrumbs = (): BreadcrumbItem[] => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const pathSet = usePathSet();
  const workstreamId = useWorkstreamId();
  const breadcrumbRouteConfig = useBreacrumbRouteConfig();

  const items: BreadcrumbItem[] = [
    // Home must be always first
    {
      type: BREADCRUMB_TYPE.STATIC,
      key: 'home',
      label: t('navbar.home'),
      to: pathSet.home,
      icon: 'ri-home-5-line',
    },
  ];

  const currentQueryParams = Object.fromEntries(searchParams.entries());

  if (workstreamId) {
    // Workstream: inserted right after Home when active
    items.push({
      type: BREADCRUMB_TYPE.WORKSTREAM,
      key: 'workstream',
      to: buildPath(pathSet.home, { queryParams: { workstreamId } }),
    });
  }

  // Handle plot group pages: show Land > Groups (clickable) > Plot Group (async)
  // TODO: MVP-9192 ensure that this logic keeps working as expected
  const isOnPlotGroupRoute =
    matchPath({ path: pathSet.details, end: false }, pathname) !== null ||
    matchPath({ path: pathSet.plotGroupPlots, end: false }, pathname) !== null;

  const plotGroupId = searchParams.get('plotGroupId');

  if (isOnPlotGroupRoute && plotGroupId) {
    items.push({
      type: BREADCRUMB_TYPE.STATIC,
      key: 'section-groups',
      label: t('navbar.groups'),
    });
    items.push({
      type: BREADCRUMB_TYPE.PLOT_GROUP,
      key: 'plot-group',
    });
  }

  // Handle plot pages: show Land > Plots (clickable) > Plot (async)
  const isOnPlotRoute = useIsPlotPage();

  if (isOnPlotRoute) {
    items.push({
      type: BREADCRUMB_TYPE.STATIC,
      key: 'section-land',
      label: t('navbar.land'),
    });
    items.push({
      type: BREADCRUMB_TYPE.STATIC,
      key: 'plots',
      label: t('navbar.plots'),
      to: buildPath(pathSet.plots, { queryParams: currentQueryParams }),
    });
    items.push({
      type: BREADCRUMB_TYPE.PLOT,
      key: 'plot',
    });
    return items;
  }

  // Handle upload pages: show Settings > Uploads (clickable) > Upload page
  const isInsituDataUploadRoute = matchPath({ path: pathSet.insituDataUpload, end: false }, pathname) !== null;
  const isMediaUploadRoute = matchPath({ path: pathSet.uploadsPlotOverview, end: false }, pathname) !== null;
  const isMediaMapsRoute = matchPath({ path: pathSet.mediaMaps, end: false }, pathname) !== null;

  if (isInsituDataUploadRoute || isMediaUploadRoute || isMediaMapsRoute) {
    items.push({
      type: BREADCRUMB_TYPE.STATIC,
      key: 'section-settings',
      label: t('navbar.settings'),
    });
    items.push({
      type: BREADCRUMB_TYPE.STATIC,
      key: 'section-uploads',
      label: t('navbar.uploads'),
      to: buildPath(pathSet.uploads, { queryParams: currentQueryParams }),
    });
    if (isInsituDataUploadRoute) {
      items.push({
        type: BREADCRUMB_TYPE.STATIC,
        key: 'insituDataUpload',
        label: t('navbar.insituData'),
      });
    }
    if (isMediaMapsRoute) {
      items.push({
        type: BREADCRUMB_TYPE.STATIC,
        key: 'mediaMaps',
        label: t('navbar.mediaMaps'),
      });
    }

    if (isMediaUploadRoute) {
      items.push({
        type: BREADCRUMB_TYPE.STATIC,
        key: 'uploadsPlotOverview',
        label: t('navbar.uploadsPlotOverview'),
      });
    }

    return items;
  }

  // Add remaining pages from the route config
  const routeEntry = breadcrumbRouteConfig.find((entry) => {
    const routePath = pathSet[entry.pathKey as keyof typeof pathSet];
    if (typeof routePath !== 'string') {
      return false;
    }
    return matchPath(routePath, pathname) !== null;
  });

  if (!routeEntry) {
    return items;
  }

  if (routeEntry.sectionLabelKey) {
    items.push({
      type: BREADCRUMB_TYPE.STATIC,
      key: `section-${routeEntry.sectionLabelKey}`,
      label: routeEntry.sectionLabelKey,
    });
  }

  const routePath = pathSet[routeEntry.pathKey as keyof typeof pathSet] as string;
  const isExactMatch = matchPath(routePath, pathname) !== null;

  items.push({
    type: BREADCRUMB_TYPE.STATIC,
    key: `page-${routeEntry.pathKey}`,
    label: routeEntry.pageLabelKey,
    to: isExactMatch ? undefined : buildPath(routePath, { queryParams: currentQueryParams }),
  });

  return items;
};
